import React, { useEffect, useState } from 'react';
import { Request } from 'helpers';

const InvitationForm = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [emails, setEmails] = useState('');
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoadingCourses(true);
      setError(null);
      try {
        const userId = window.__USER_ID__;
        const { data } = await Request.get(`/api/v1/users/${userId}/private_courses`);
        setCourses(data || []);
      } catch (e) {
        setError('Failed to load courses');
      } finally {
        setIsLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        email: emails,
        course_id: selectedCourse
      };
      console.log('Sending invitation with:', body);
      const response = await Request.post('/invitations', {}, body);
      console.log('Response:', response);
      
      if (response.success) {
        setSuccess('Invitations sent successfully!');
        setEmails('');
        setSelectedCourse('');
      } else {
        setError(response.message || 'Failed to send invitations');
      }
    } catch (e) {
      console.error('Error sending invitation:', e);
      setError('Failed to send invitations');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="invitation-form-container">
      <style jsx>{`
        .invitation-form .form-control,
        .invitation-form select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          border: 1px solid #9ca3af;
          border-radius: 0.375rem;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }
        
        .invitation-form .form-control:focus,
        .invitation-form select:focus {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .invitation-form select:disabled {
          background-color: #e9ecef;
          opacity: 0.65;
          cursor: not-allowed;
        }
        
        .invitation-form select option {
          padding: 8px 12px;
          border-bottom: 1px solid #f8f9fa;
          background-color: white;
          color: #333;
        }
        
        .invitation-form select option:hover,
        .invitation-form select option:focus,
        .invitation-form select option:active {
          background-color: #6343d8 !important;
          color: white !important;
        }
        
        .invitation-form select option:checked,
        .invitation-form select option:selected {
          background-color: #6343d8 !important;
          color: white !important;
        }
        
        /* Ensure dropdown opens downward by default */
        .invitation-form select {
          direction: ltr;
        }
        
        /* Custom scrollbar for dropdown */
        .invitation-form select::-webkit-scrollbar {
          width: 8px;
        }
        
        .invitation-form select::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .invitation-form select::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .invitation-form select::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
      
      <form onSubmit={handleSubmit} className="invitation-form">
        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success mb-4 d-flex justify-content-between align-items-center" role="alert">
            <span>{success}</span>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setSuccess(null)}
              aria-label="Close"
            ></button>
          </div>
        )}
        
        <div className="course-form-group mb-4">
          <label className="form-label fw-bold mb-2">
            Emails <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            value={emails}
            onChange={e => setEmails(e.target.value)}
            placeholder="Enter comma-separated emails (e.g., email1@example.com, email2@example.com)"
            required
            rows="4"
            style={{ resize: 'vertical' }}
          />
          <small className="form-text text-muted">
            You can invite up to 10 students by separating email addresses with commas.
          </small>
        </div>
        
        <div className="course-form-group mb-4">
          <label className="form-label fw-bold mb-2">
            Select Course <span className="text-danger">*</span>
          </label>
          <select
            className="form-control form-select"
            value={selectedCourse}
            onChange={e => setSelectedCourse(e.target.value)}
            required
            disabled={isLoadingCourses}
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3e%3cpath fill=\'none\' stroke=\'%23343a40\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'m1 6 7 7 7-7\'/%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '16px 12px',
              paddingRight: '2.5rem',
              cursor: 'pointer',
              minHeight: '45px'
            }}
          >
            {isLoadingCourses ? (
              <option value="" disabled>Loading courses...</option>
            ) : (
              <>
                <option value="" disabled style={{ display: 'none' }}>
                  Choose a course to invite students to
                </option>
                {courses.map(course => (
                  <option key={course.id} value={course.id} style={{ padding: '8px 12px' }}>
                    {course.title}
                  </option>
                ))}
              </>
            )}
          </select>
          {courses.length === 0 && !isLoadingCourses && (
            <small className="form-text text-muted">
              No private courses available. Create a private course first.
            </small>
          )}
        </div>
        
        <div className="d-flex justify-content-start">
          <button 
            type="submit" 
            className="btn btn-primary px-4 py-2" 
            disabled={isSubmitting || isLoadingCourses}
            style={{ minWidth: '150px' }}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvitationForm;
